import { useEffect, useRef } from "react"
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom"

import moment from "moment/moment"
import Delete from "pages/product/series/delete"
import Filter from "pages/product/series/filter"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { lang } from "config"
import { Pencil, Plus } from "components/icons/outline"
import { usePage } from "contexts/page-context"
import useProductSeriesRepositories from "repositories/product-series"
import { ArrowNarrowRight } from "components/icons/outline"
import Table from "components/table"
import useDebouncedSearchParam from "hooks/use-debounced-search-param"

export default function List() {
    // Logged in user ability
    const { can, isSuper, currentBranch } = useAuth()
    // Configuration
    const { configuration } = usePage()

    // Transition
    const { title, transition, setTransition } = useOutletContext()

    // React router navigate hook
    const navigate = useNavigate()

    // React router location hook
    const location = useLocation()

    // Query parameters
    const [searchParams, setSearchParams] = useSearchParams()

    const searchInput = useRef()

    const page = searchParams.get('page')
    const search = searchParams.get('search')
    const [searchTemp, setSearchTemp, submitSearch] = useDebouncedSearchParam({ search, searchParams, setSearchParams })
    const series = searchParams.get('series')
    const from = searchParams.get('from')
    const to = searchParams.get('to')

    const orderBy = searchParams.get('order_by')
    const direction = searchParams.get('direction')

    const { data: productSeriesData, isLoading: isLoadingProductSeriesData, mutate: mutateProductSeriesData } = useProductSeriesRepositories({
        page,
        search,
        series,
        from,
        to,

        order_by: orderBy,
        direction
    })

    useEffect(() => {
        window.history.replaceState({}, document.title)

        if (transition.reverse) {
            setTransition({ type: 'slide', reverse: false })
        }
    }, [])

    useEffect(() => {
        searchInput.current.focus()
    }, [search])

    // Watch changes on petty cash data and loading state
    useEffect(() => {
        if (search === "") {
            navigate(``, { replace: true })
        }

        if (isLoadingProductSeriesData) {
            nProgress.start()
        } else {
            nProgress.done()
        }
    }, [productSeriesData, isLoadingProductSeriesData])

    // Data filter handlers
    const updateFilter = (value) => {
        setSearchParams(value)
    }

    const removeFilter = () => {
        setSearchParams({})
    }

    // Data sort handlers
    const toggleSort = (value) => {
        if ((direction === null || direction === 'desc') || orderBy !== value) {
            setSearchParams({
                order_by: value,
                direction: 'asc'
            })
        } else {
            setSearchParams({
                order_by: value,
                direction: 'desc'
            })
        }
    }

    // Data list props
    const dataListHeaders = [
        ...configuration('hierarchy').find((row) => row.key === 'product-brand').value ? [{
            isAction: false,
            content: "Product Brand",
            sortKey: "brand.name"
        }] : [],
        {
            isAction: false,
            content: "Series",
            sortKey: "series.name"
        },
        {
            isAction: false,
            content: lang.created_at,
            sortKey: "created_at"
        },
        {
            isAction: false,
            content: lang.updated_at,
            sortKey: "updated_at"
        },
        {
            isAction: true,
            content: null
        }
    ]

    const dataListColumns = [
        ...configuration('hierarchy').find((row) => row.key === 'product-brand').value ? [{
            type: "default",
            bold: false,
            content: (value) => (
                <div className="flex flex-col space-y-1">
                    <div>{value.product_brand?.product_type?.name}</div>
                    <div className="font-medium text-neutral-900">{value.product_brand?.brand?.name}</div>
                </div>
            )
        }] : [],
        {
            type: "default",
            bold: true,
            content: (value) => value.series?.name
        },
        {
            type: "default",
            bold: false,
            content: (value) => moment(value.created_at).format('MMMM D, YYYY')
        },
        {
            type: "default",
            bold: false,
            content: (value) => moment(value.updated_at).format('MMMM D, YYYY')
        },
        {
            type: "action",
            content: (value) => (
                <>
                    {can('product-series.update') && (
                        <Link to={`${value.id}/edit`} state={{ back: title, from: location?.pathname, transition: 'slide' }} className="inline-flex items-center p-1 text-white transition rounded-full bg-brand-800 active:hover:scale-90">
                            <Pencil className="w-6 h-6" />
                        </Link>
                    )}

                    {can('product-series.delete') && (
                        <Delete data={value} onSuccess={() => mutateProductSeriesData()} />
                    )}
                </>
            )
        }
    ]

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center sm:space-x-2">
                    <div className="relative hidden sm:block">
                        <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <circle cx={10} cy={10} r={7}></circle>
                                <line x1={21} y1={21} x2={15} y2={15}></line>
                            </svg>
                        </div>
                        <input ref={searchInput} onChange={(e) => setSearchTemp(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSearch() } }} value={searchTemp} type="text" placeholder={`${lang.search} Product Series`} autoComplete="off" className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200" />
                    </div>
                    <Filter onSubmit={updateFilter} onRemove={removeFilter} data={Object.fromEntries(Object.entries({ series, from, to }).filter(([_, v]) => v != null))} />
                </div>
                <div className="flex items-center space-x-2">
                    {can('product-series.create') && (
                        <div>
                            <Link to="create" onClick={() => { setTransition({ type: 'slide', reverse: true }) }} state={{ back: title, from: location?.pathname, transition: { type: 'slide' } }} className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-brand-800 rounded-xl active:hover:scale-90">
                                <Plus className="w-4 h-4" stroke={1.5} />
                                <span>{lang.create}</span>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
            <Table isLoading={isLoadingProductSeriesData} data={productSeriesData?.data ?? []} headers={dataListHeaders} columns={dataListColumns} key={(value) => value.id} isEmpty={productSeriesData?.data.length === 0 && !search && !isLoadingProductSeriesData} isEmptyOnSearching={productSeriesData?.data.length === 0 && search && !isLoadingProductSeriesData} toggleSort={toggleSort} orderBy={orderBy} direction={direction} />
            <Pagination links={productSeriesData?.links} from={productSeriesData?.from} to={productSeriesData?.to} total={productSeriesData?.total} />
        </div>
    )
}
